import { FC } from 'react';
import { Control, Controller, UseFormSetValue, useWatch } from 'react-hook-form';
import { Box } from 'enova-frontend-components';
import { useTranslation } from 'react-i18next';

import { AboutFormData } from '../../types';
import { ByggType, JaNeiVetIkke, PlasseringAvEnhetIByggVertikalt } from '../../../../../utils/registerEnums';
import { SelectableAreaWrapper } from '../../heating/components/checkboxArea';
import { EnumRadioGroup } from '../../heating/components/enumRadioGroup';
import { EnumRadioButton } from '../../heating/components/enumRadioButton';

import { HeatedBasementRadiogroup } from './heatedBasementRadioGroup';
import { AdjoiningRoomSelect } from './adjoiningRoomField';

interface BasementRadiogroupProps {
  control: Control<AboutFormData>;
  setValue: UseFormSetValue<AboutFormData>;
}

export const BasementRadioGroup: FC<BasementRadiogroupProps> = ({ control, setValue }) => {
  const { t } = useTranslation();

  const byggType = useWatch({ control, name: 'bygginfo.type' });
  const plasseringAvEnhetIByggVertikalt = useWatch({
    control,
    name: 'bygginfo.verticalPlacement'
  });

  if (
    byggType === ByggType.Leilighet &&
    plasseringAvEnhetIByggVertikalt != null &&
    plasseringAvEnhetIByggVertikalt !== PlasseringAvEnhetIByggVertikalt.NedersteEtasje
  ) {
    return null;
  }

  return (
    <Controller
      name="bygginfo.hasBasement"
      control={control}
      rules={{
        validate: (value) => {
          if (value === undefined) {
            return t('register.errorMessage.field.harBoligenKjeller');
          }
        }
      }}
      render={({ field: { value, onChange }, fieldState: { error } }) => {
        const hasValue = value != null;

        const handleChange = (value: string) => {
          const booleanValue = value === JaNeiVetIkke.Ja;

          // When the user selects 'no' we also need to update 'heleEllerDelerAvKjellerErOppvarmet'
          if (!booleanValue) {
            setValue('bygginfo.isHeatedBasement', booleanValue);
          } else {
            // When the user selects 'no' we also need to clear 'basementAdjoiningRoom' value
            setValue('bygginfo.basementAdjoiningRoom', undefined);
            // If hasBasement is checked we set default value for isHeatedBasement to false
            setValue('bygginfo.isHeatedBasement', false);
          }

          onChange(booleanValue);
        };

        // Needed to set empty selection to radio. Passing value directly will
        // set option to "Nei" when value is undefined.
        const enumValue = value === true ? JaNeiVetIkke.Ja : value === false ? JaNeiVetIkke.Nei : undefined;

        return (
          <SelectableAreaWrapper isSelected={hasValue} pt={3}>
            <EnumRadioGroup
              labelKey="register.bygningsdetaljer.harBoligenKjeller.label"
              helperTextKey="register.bygningsdetaljer.harBoligenKjeller.information"
              value={enumValue}
              onChange={handleChange}
              error={error}
            >
              <EnumRadioButton value={JaNeiVetIkke.Ja} i18nPrefix="register" />
              <EnumRadioButton value={JaNeiVetIkke.Nei} i18nPrefix="register" />
            </EnumRadioGroup>

            {hasValue && value && (
              <Box p={5}>
                <HeatedBasementRadiogroup control={control} />
              </Box>
            )}
            {hasValue && !value && (
              <Box p={5}>
                <AdjoiningRoomSelect control={control} />
              </Box>
            )}
          </SelectableAreaWrapper>
        );
      }}
    />
  );
};
